import { ThumbsDown } from 'lucide-react';
import type { DraftFeedbackStats } from '../../services/usageApi';
import { StatTile } from './StatTile';
import { DraftFeedbackSamples } from './DraftFeedbackSamples';
import { formatPercent } from './chartTokens';

/** `role_logistics` → `Role logistics`. Codes are for machines; this table is read. */
function humanise(code: string): string {
  const words = `${code || 'unknown'}`.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What recruiters think of the drafts the tab produces.
 *
 * Grouped by CATEGORY and by SOURCE, worst first, because that is the actionable shape: "model drafts
 * for process questions are rated down 70% of the time" names a prompt to fix, whereas an overall
 * satisfaction number names nothing.
 *
 * Rates alongside counts on purpose — two thumbs down out of two matters more than ten out of two
 * hundred, and a raw count hides exactly that. A group with no votes is absent rather than shown as
 * 0%, since "0% unhappy" out of nothing is a lie.
 *
 * Grouped by TAB as well, because this widget is spreading to other boards and one blended
 * satisfaction number across different surfaces would be unreadable.
 *
 * Every vote is shown with the candidate's mail and the draft it was a verdict on — a comment alone
 * cannot be acted on. All of that is candidate-adjacent content: it is shown here because this page
 * is permission-gated (enforced even in audit-only mode) and it is never sent to an analytics sink.
 */
export function DraftFeedbackSection({ stats }: { stats: DraftFeedbackStats }) {
  if (stats.total === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-8 text-center">
        <ThumbsDown className="mx-auto h-8 w-8 text-zinc-300" />
        <p className="mt-3 text-sm font-medium text-zinc-700">No draft ratings in this range</p>
        <p className="mt-1 text-xs text-zinc-500">
          Recruiters rate a draft from the card it appears on, in Candidate requests.
        </p>
      </div>
    );
  }

  const table = (title: string, rows: DraftFeedbackStats['byCategory']) => (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">{title}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500">
              <th className="py-1 pr-4 font-medium">Kind</th>
              <th className="py-1 pr-4 font-medium">👍</th>
              <th className="py-1 pr-4 font-medium">👎</th>
              <th className="py-1 font-medium">Rated down</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-zinc-100">
                <td className="py-1.5 pr-4 text-zinc-700">{humanise(r.name)}</td>
                <td className="py-1.5 pr-4 text-zinc-600">{r.up}</td>
                <td className="py-1.5 pr-4 text-zinc-600">{r.down}</td>
                <td className="py-1.5 font-medium text-zinc-800">{formatPercent(r.downRate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="Drafts rated" value={`${stats.total}`} />
        <StatTile label="Useful" value={`${stats.up}`} />
        {/* Null means nothing was rated; an em dash is honest where "0%" would not be. */}
        <StatTile
          label="Rated down"
          value={stats.downRate === null ? '—' : formatPercent(stats.downRate)}
        />
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {table('By question type', stats.byCategory)}
        {table('By draft source', stats.bySource)}
      </div>
      {/* One row today. It earns its place the moment a second board starts collecting votes. */}
      {stats.byTab.length > 0 && table('By board tab', stats.byTab)}
      <DraftFeedbackSamples samples={stats.samples} />
    </div>
  );
}
